<template>
    <AllowPermission :permission="`CMS_VIEW_${permissionCategory}`">
        <Card>
            <div :class="[{ dark: theme == THEMES.DARK }]">
                <div class="crud-list-header d-flex justify-content-between align-items-center mb-4">
                    <div class="crud-title">
                        <span v-if="showForm && editMode">Update Entry</span>
                        <span v-else-if="showForm">Create Entry</span>
                        <span v-else>All List</span>
                    </div>
                    <div class="d-flex">
                        <AllowPermission :permission="`CMS_CREATE_${permissionCategory}`">
                            <Button
                                @click="enableCreateForm"
                                condensed
                                class="create-btn text-white font-bold mr-2"
                                :noMargin="true"
                                v-if="!showForm && createUrl && !onlyEdit"
                            >
                                Create {{ title }} <i class="fas fa-plus ml-1"></i>
                            </Button>
                        </AllowPermission>
                        <!-- Custom action buttons -->
                        <Button
                            v-for="(action, index) in customActions"
                            :key="index"
                            @click="action.action"
                            condensed
                            :class="[action.class, 'mr-2']"
                            :noMargin="true"
                            v-if="!showForm && !onlyEdit"
                        >
                            {{ action.title }} <i :class="['fas', action.icon, 'ml-1']" v-if="action.icon"></i>
                        </Button>
                        <Button v-if="showForm" @click="disableEditing(true)" condensed class="cancel-btn text-white font-bold" :noMargin="true">
                            Cancel
                        </Button>
                    </div>
                </div>
                <div v-if="showForm">
                    <Form
                        :fields="createFields"
                        :title="title"
                        :submitUrl="createUrl"
                        :updateRootUrl="updateRootUrl"
                        @success="handleCreateSuccess"
                        @cancel="disableEditing(true)"
                        :editMode="editMode"
                        :editData="editData"
                    />
                </div>
                <CrudListTable
                    v-else
                    :loading="loading"
                    :loadError="loadError"
                    :title="title"
                    :rows="tableRows"
                    :columns="columnsWithActions"
                    :total-records="totalRecords"
                    :pagination-options="paginationOptions"
                    :table-mode="tableMode"
                    :search-options="searchOptions"
                    :theme="theme"
                    :themes="THEMES"
                    :permission-category="permissionCategory"
                    :only-btn-icons="onlyBtnIcons"
                    :show-edit-btn="showEditBtn"
                    :delete-root-url="deleteRootUrl"
                    :custom-action-permission="customActionPermission"
                    :custom-action-title="customActionTitle"
                    :custom-action-icon="customActionIcon"
                    :custom-action-icon-only="customActionIconOnly"
                    :processing-custom-action-id="processingCustomActionId"
                    :processing-custom-action="processingCustomAction"
                    @retry="fetchData"
                    @edit="handleEdit"
                    @custom-action="$emit('handleCustomAction', $event)"
                    @delete="deleteDataItem"
                    @page-change="handlePageChange"
                    @per-page-change="handlePerPageChange"
                    @sort-change="handleSortChange"
                    @search="handleSearch"
                />
            </div>
        </Card>
    </AllowPermission>
</template>

<script>
import "material-design-icons-iconfont"
import Card from "../../components/UI/Card.vue"
import Button from "../../components/UI/Button.vue"
import Form from "../../components/crud/form.vue"
import CrudListTable from "../../components/crud/CrudListTable.vue"
import { http } from "../../utils/http"

import { mapActions, mapState } from "vuex"
import AllowPermission from "../layout/AllowPermission.vue"
import { scrollPageToTop } from "../../utils/scroll-container"
import { confirmDialog } from "../../utils/common"
import { buildServerSideParams, extractRows, extractTotalRows } from "./table-state"

export default {
    components: { Form, Card, Button, CrudListTable, AllowPermission },

    data() {
        return {
            dataItems: [],
            name: "",
            showForm: false,
            initialized: false,
            processing: false,
            columns: [],
            loading: false,
            loadError: "",
            editMode: false,
            editData: null,
            tableState: {
                page: 1,
                perPage: 10,
                searchTerm: "",
                sortBy: "",
                sortOrder: "",
            },
            totalRecords: 0,
            tableSearchTimer: null,
            fetchRequestId: 0,
        }
    },
    props: {
        permission: {
            type: String,
            default: "",
        },
        applyPermissions: {
            type: Boolean,
            default: true,
        },
        onlyBtnIcons: {
            type: Boolean,
            default: false,
        },
        customActions: {
            type: Array,
            default: () => [],
        },
        customActionTitle: {
            type: String,
            default: "",
        },
        customActionIcon: {
            type: String,
            default: "fa-key",
        },
        customActionIconOnly: {
            type: Boolean,
            default: false,
        },
        customActionPermission: {
            type: String,
            default: "",
        },
        processingCustomActionId: {
            type: Number,
            default: null,
        },
        processingCustomAction: {
            type: Boolean,
            default: false,
        },
        backRoute: {
            type: String,
            default: "",
        },
        navigateToCustomCreate: {
            type: Boolean,
            default: false,
        },
        editingFormatter: {
            type: Function,
            default: (data) => {
                return data
            },
        },
        getFormatter: {
            type: Function,
            default: (data) => {
                return data
            },
        },
        dataItemFilter: {
            type: Function,
            default: (data) => {
                return data
            },
        },
        allowGoBack: {
            type: Boolean,
            default: false,
        },
        onlyEdit: {
            type: Boolean,
            default: false,
        },
        onlyEditData: {
            type: Object,
            default: () => null,
        },
        hideActions: {
            type: Boolean,
            default: () => false,
        },
        showEditBtn: {
            type: Boolean,
            default: () => true,
        },
        editRouteName: { type: String, default: "" },
        createFields: {
            type: Array,
            default: () => [],
        },
        title: {
            type: String,
            default: "",
        },
        fetchUrl: {
            type: String,
            default: "",
        },
        createUrl: {
            type: String,
            default: "",
        },
        updateRootUrl: {
            type: String,
            default: "",
        },
        deleteRootUrl: {
            type: String,
            default: "",
        },
        defaultEnableCreateForm: {
            type: Boolean,
            default: false,
        },
        reloadOnSuccess: {
            type: Boolean,
            default: false,
        },
        onDisableRedirectUrl: {
            type: String,
            default: "",
        },
        onSuccessRedirectUrl: {
            type: String,
            default: "",
        },
        viewColumns: {
            type: Array,
            default: () => [
                // {
                //     label: "Id",
                //     field: "id",
                //     type: "number",
                // },
                // {
                //     label: "Name",
                //     field: "name",
                // },
            ],
        },
        serverSide: {
            type: Boolean,
            default: false,
        },
        defaultPerPage: {
            type: Number,
            default: 10,
        },
    },
    computed: {
        tableRows() {
            return this.dataItemFilter(this.dataItems)
        },
        tableMode() {
            return this.serverSide ? "remote" : "local"
        },
        paginationOptions() {
            return {
                enabled: true,
                mode: "records",
                perPage: this.tableState.perPage,
            }
        },
        searchOptions() {
            return {
                enabled: true,
                skipDiacritics: true,
            }
        },
        columnsWithActions() {
            if (this.hideActions) return this.columns
            return this.columns.concat({
                field: "actions",
                label: "Actions",
                thClass: "crud-actions-column",
                tdClass: "crud-actions-column",
                sortable: false,
            })
        },
        ...mapState("misc", ["theme", "THEMES"]),
        permissionCategory() {
            // get the rest of the permissions
            const prmCat = this.permission ? this.permission.split("_").slice(1).join("_") : ""
            return prmCat
        },
    },
    mounted() {
        this.setData({ pageTitle: this.title })
        this.columns = this.viewColumns
        this.tableState.perPage = this.defaultPerPage
        if (this.onlyEdit) {
            this.enableEditing(this.onlyEditData)
        } else {
            this.fetchData()
        }

        if (this.defaultEnableCreateForm) {
            this.enableCreateForm()
        }
    },
    beforeDestroy() {
        if (this.tableSearchTimer) {
            clearTimeout(this.tableSearchTimer)
            this.tableSearchTimer = null
        }
    },
    methods: {
        ...mapActions("misc", ["setData"]),
        navigatePage(routeObj) {
            window.location.href = this.$router.resolve(routeObj).href
        },
        handleEdit(props) {
            if (this.editRouteName) {
                this.navigatePage({
                    name: this.editRouteName,
                    params: { id: props.row.id },
                })
            } else {
                this.enableEditing(this.dataItems.find((item) => item.id == props.row.id))
            }
        },
        async enableCreateForm(warn = false) {
            if (this.navigateToCustomCreate) {
                this.$router.push(this.createUrl)
                return
            }
            this.editData = null

            // for new state
            const showFormOriginal = this.showForm
            this.showForm = false

            setTimeout(() => {
                this.showForm = showFormOriginal
                if (this.showForm && !this.editMode) {
                    this.showForm = false
                } else {
                    this.showForm = true
                }
                this.editMode = false
            }, 0)
        },
        async enableEditing(editItem) {
            this.disableEditing()

            setTimeout(() => {
                this.editMode = true
                this.editData = this.editingFormatter(editItem)
                this.showForm = true
            }, 0)
            scrollPageToTop()
        },
        async disableEditing(cancelMode) {
            if (this.onDisableRedirectUrl) {
                window.location.href = this.onDisableRedirectUrl
            }
            let confirmOperation = true
            if (cancelMode) {
                confirmOperation = await confirmDialog({
                    title: "Cancel changes?",
                    text: "Unsaved changes on this form will be discarded.",
                    confirmButtonText: "Discard changes",
                })
            }
            if (!confirmOperation) return
            scrollPageToTop()
            if (this.onlyEdit) {
                if (this.allowGoBack && cancelMode) {
                    this.$router.go(-1)
                }
            } else {
                this.editMode = false
                this.editData = null
                this.showForm = false
            }
        },
        async handleCreateSuccess(result) {
            if (this.onlyEdit) {
                if (this.allowGoBack) {
                    if (this.backRoute) {
                        return this.$router.push(this.backRoute)
                    }
                    this.$router.go(-1)
                }
                return
            }
            if (this.editMode) {
                this.dataItems = this.dataItems.map((item) => {
                    if (item.id == result.id) {
                        return result
                    }
                    return item
                })
            } else {
                this.dataItems.unshift(result)
            }

            if (this.reloadOnSuccess) {
                if (this.onSuccessRedirectUrl) {
                    window.location.href = this.onSuccessRedirectUrl
                } else {
                    await this.fetchData()
                    this.$emit("success", { result, editMode: this.editMode })
                    this.disableEditing()
                }
            } else if (this.serverSide) {
                await this.fetchData()
                this.$emit("success", { result, editMode: this.editMode })
                this.disableEditing()
            } else {
                this.$emit("success", { result, editMode: this.editMode })
                this.disableEditing()
            }
        },
        getServerSideParams() {
            return buildServerSideParams(this.tableState)
        },
        extractRows(responseData) {
            return extractRows(responseData)
        },
        extractTotalRows(responseData, rows) {
            return extractTotalRows(responseData, rows, this.serverSide)
        },
        async fetchData() {
            const requestId = ++this.fetchRequestId
            this.loading = true
            this.loadError = ""
            try {
                const res = await http.get(
                    this.fetchUrl,
                    this.serverSide
                        ? {
                              params: this.getServerSideParams(),
                          }
                        : undefined
                )
                if (requestId !== this.fetchRequestId) return
                const rows = this.extractRows(res.data)
                this.dataItems = this.getFormatter(rows)
                this.totalRecords = this.extractTotalRows(res.data, rows)
            } catch (err) {
                if (requestId !== this.fetchRequestId) return
                console.log("error in loading data", err)
                this.loadError = "Could not load this list."
            } finally {
                if (requestId === this.fetchRequestId) {
                    this.initialized = true
                    this.loading = false
                }
            }
        },
        async deleteDataItem(id) {
            try {
                const confirmed = await confirmDialog({
                    title: `Delete ${this.title}?`,
                    text: "This record will be permanently removed.",
                    confirmButtonText: "Delete",
                })
                if (!confirmed) return

                await http.delete(`${this.deleteRootUrl}/${id}`)
                this.$toast.success(this.title + " Deleted Successfully!")
                if (this.serverSide) {
                    await this.fetchData()
                } else {
                    this.dataItems = this.dataItems.filter((item) => item.id !== id)
                    this.totalRecords = this.dataItems.length
                }
                this.disableEditing()
            } catch (err) {
                console.log("error in deleting " + this.title + " item", err)
                this.$toast.error("Some error occured")
            }
        },
        handlePageChange(params) {
            if (!this.serverSide) return
            this.tableState.page = params.currentPage
            this.fetchData()
        },
        handlePerPageChange(params) {
            if (!this.serverSide) return
            this.tableState.perPage = params.currentPerPage
            this.tableState.page = 1
            this.fetchData()
        },
        handleSortChange(params) {
            if (!this.serverSide) return
            const sort = Array.isArray(params) ? params[0] : null
            this.tableState.sortBy = sort && sort.field ? sort.field : ""
            this.tableState.sortOrder = sort && sort.type ? sort.type : ""
            this.tableState.page = 1
            this.fetchData()
        },
        handleSearch(params) {
            if (!this.serverSide) return
            this.tableState.searchTerm = params.searchTerm || ""
            this.tableState.page = 1
            if (this.tableSearchTimer) {
                clearTimeout(this.tableSearchTimer)
            }
            this.tableSearchTimer = setTimeout(() => {
                this.fetchData()
            }, 250)
        },
    },
}
</script>

<style lang="scss">
@use "sass:color";
@import "../../assets/variables.scss";
/* removed duplicate import */
.crud-title {
    font-size: 1.25rem;
    font-weight: 600;
    color: $primaryColor;
    position: relative;
    padding-bottom: 6px;
    
    &:after {
        content: '';
        position: absolute;
        bottom: 0;
        left: 0;
        width: 40px;
        height: 3px;
        background-color: $secondaryColor;
        border-radius: 2px;
    }
    
    .dark & {
        color: $secondaryColor;
    }
}
.vgt-inner-wrap {
    // border-radius: 10px;
    overflow: hidden;
    border: none !important;
    // box-shadow: 0 0 6px 0 rgba(0, 0, 0, 0.3);
    box-shadow: none;
}
.vgt-responsive {
    overflow-x: auto !important;
    overflow-y: hidden;
    -webkit-overflow-scrolling: touch;
}
.vgt-global-search {
    border: none !important;
    border-radius: 5px;
    padding: 5px 10px;
    input {
        border: none;
        background: transparent !important;
    }
    input::placeholder {
        color: rgba($textColor, 0.7);
    }
    .magnifying-glass::before {
        border: 2px solid $lightBorderColor !important;
    }
}
.vgt-wrap__footer {
    border-top: 1px solid $lightBorderColor !important;
    padding: 12px !important;
    
    .footer__navigation__page-btn {
        color: $primaryColor !important;
        
        &:hover {
            color: $secondaryColor !important;
        }
        
        &.disabled {
            color: rgba($textColor, 0.5) !important;
            opacity: 0.6 !important;
        }
    }
    
    .footer__navigation__page-info {
        color: $primaryColor !important;
    }
    
    .dark_theme & {
        border-color: $darkBorderColor !important;
        
        .footer__navigation__page-btn {
            color: $secondaryColorDark !important;
            
            &:hover {
                color: color.adjust($secondaryColorDark, $lightness: 10%) !important;
            }
            
            &.disabled {
                color: rgba($darkTextColor, 0.5) !important;
            }
        }
        
        .footer__navigation__page-info {
            color: $darkTextColor !important;
        }
    }
}

.vgt-inner-wrap td,
.vgt-inner-wrap th,
.vgt-inner-wrap th span,
.vgt-wrap__footer {
    color: $textColor !important;
    background: $lightCardBg !important;
}

.vgt-table {
    border: none !important;
    font-size: 10px;
    min-width: 760px;

    tr {
        height: 48px !important; /* Set fixed row height */
    }
    
    td {
        padding-top: 6px !important;
        padding-bottom: 6px !important;
        vertical-align: middle !important;
    }
    
    &.dark {
        border-color: $darkBorderColor;
        th,
        td {
            color: $darkTextColor;
            background: $darkCardBg;
            border-color: $darkBorderColor;
        }
    }
}
.crud-actions-column {
    min-width: 150px;
}
.dark {
    .vgt-global-search {
        .magnifying-glass {
            border: 2px solid $darkBorderColor !important;
        }
        .magnifying-glass::before {
            border: 2px solid $darkBorderColor !important;
        }

        input::placeholder {
            color: rgba($darkTextColor, 0.7);
        }
    }
    .vgt-global-search__input input {
        background: $darkInputBg;
        color: $darkTextColor;
    }
    .vgt-global-search,
    .vgt-wrap__footer {
        color: $darkTextColor;
        background: $darkCardBg;
        border-color: $darkBorderColor;
    }
    .vgt-inner-wrap td,
    .vgt-inner-wrap th,
    .vgt-inner-wrap th span,
    .vgt-wrap__footer {
        color: $darkTextColor !important;
        background: $darkCardBg !important;
    }
}
</style>
<style lang="scss">
@use "sass:color";
@import "../../assets/variables.scss";
.footer__row-count__label,
.footer__row-count__select,
.footer__navigation__page-info,
.footer__navigation__page-btn span {
    font-size: 0.85rem !important;
}

.action-buttons-container {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 8px; /* Explicit spacing between all child elements */
    white-space: nowrap;
}

.action-btn {
    margin: 0 !important;
    padding: 6px 12px !important;
    font-size: 0.75em !important;
    height: 30px !important;
    border-radius: 4px !important;
    display: inline-flex !important;
    align-items: center !important;
    justify-content: center !important;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1) !important;
    transition: all 0.2s ease-in-out !important;
}

.compact-action-btn {
    width: 30px !important;
    min-width: 30px !important;
    padding: 0 !important;
}

.email-btn {
    background-color: $primaryColor !important;
    color: white !important;
    min-width: 160px !important;
    
    &:hover:not([disabled]) {
        background-color: color.adjust($primaryColor, $lightness: -5%) !important;
    }
    
    &[disabled] {
        opacity: 0.7 !important;
    }
}

.permissions-btn {
    background-color: $secondaryColor !important; /* Secondary color from variables */
    color: white !important;
    
    &:hover {
        background-color: color.adjust($secondaryColor, $lightness: -5%) !important;
    }
}

.edit-btn {
    background-color: $primaryColor !important;
    color: white !important;
    min-width: 70px !important;
    
    &:hover {
        background-color: color.adjust($primaryColor, $lightness: -5%) !important;
    }
}

.delete-btn {
    background-color: $dangerColor !important;  /* New third color for our scheme */
    color: white !important;
    
    &:hover {
        background-color: color.adjust($dangerColor, $lightness: -5%) !important;
    }
}

@media (max-width: 768px) {
    .crud-list-header {
        align-items: stretch !important;
        flex-direction: column;
        gap: 12px;
    }

    .crud-list-header > .d-flex {
        flex-wrap: wrap;
        gap: 8px;
    }

    .crud-list-header .create-btn,
    .crud-list-header .delete-btn,
    .crud-list-header .cancel-btn {
        flex: 1 1 auto;
        margin-right: 0 !important;
    }

    .vgt-table {
        min-width: 700px;
    }

    .crud-actions-column {
        position: sticky !important;
        right: 0;
        z-index: 3;
        min-width: 96px;
        box-shadow: -8px 0 12px rgba(255, 255, 255, 0.9);
    }

    .vgt-table thead .crud-actions-column {
        z-index: 4;
    }

    .dark .crud-actions-column {
        box-shadow: -8px 0 12px rgba(21, 38, 35, 0.88);
    }

    .action-buttons-container {
        gap: 6px;
    }

    .action-buttons-container .action-btn {
        width: 36px !important;
        min-width: 36px !important;
        padding: 0 !important;
    }

    .action-buttons-container .action-btn span {
        display: none !important;
    }

    .action-buttons-container .action-btn i {
        margin-right: 0 !important;
    }
}

.create-btn {
    background-color: $primaryColor !important;
    color: white !important;
    font-weight: 600 !important;
    border-radius: 6px !important;
    padding: 10px 16px !important;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1) !important;
    transition: all 0.2s ease-in-out !important;
    
    &:hover {
        background-color: color.adjust($primaryColor, $lightness: -5%) !important;
        box-shadow: 0 3px 6px rgba(0, 0, 0, 0.15) !important;
    }
}

.cancel-btn {
    background-color: $dangerColor !important;
    color: white !important;
    font-weight: 600 !important;
    border-radius: 6px !important;
    padding: 10px 16px !important;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1) !important;
    transition: all 0.2s ease-in-out !important;
    
    &:hover {
        background-color: color.adjust($dangerColor, $lightness: -5%) !important;
        box-shadow: 0 3px 6px rgba(0, 0, 0, 0.15) !important;
    }
}
</style>
